declare global {
  interface Window {
    gtag: (...args: any[]) => void
    dataLayer: any[]
  }
}

const gtag = (...args: any[]) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag(...args)
  }
}

class GoogleAnalytics {
  private measurementId: string
  private isInitialized = false
  private isBlocked = false
  private scriptLoadTimeout = 10000 // 10 seconds timeout

  constructor() {
    this.measurementId = import.meta.env.VITE_GA_TRACKING_ID || ''
  }

  async init(): Promise<void> {
    if (this.isInitialized) {
      return
    }

    // Check if we have a tracking ID
    if (!this.measurementId) {
      console.warn('Google Analytics: No tracking ID provided. Set VITE_GA_TRACKING_ID environment variable.')
      return
    }

    try {
      // Load Google Analytics script dynamically
      await this.loadGoogleAnalyticsScript()

      // Wait a bit for the script to fully initialize
      await new Promise(resolve => setTimeout(resolve, 100))

      // Check if gtag function is available (script loaded successfully)
      if (typeof window.gtag === 'undefined') {
        // Script might be blocked, but we'll still try to initialize
        this.isBlocked = true
        if (import.meta.env.DEV) {
          console.warn('Google Analytics: Script may be blocked by ad blocker or privacy extension')
        }
      }

      // Initialize gtag
      window.dataLayer = window.dataLayer || []
      if (typeof window.gtag === 'undefined') {
        // Fallback: create a mock gtag that queues events
        window.gtag = function() {
          window.dataLayer.push(arguments)
        }
      }
      
      window.gtag('js', new Date())
      window.gtag('config', this.measurementId, {
        page_title: document.title,
        page_location: window.location.href,
      })

      // Verify initialization by checking if dataLayer is working
      this.verifyInitialization()

      // Log which tracking ID is being used
      if (import.meta.env.DEV) {
        console.log(`Google Analytics initialized with tracking ID: ${this.measurementId}`)
        console.log(`Environment: ${import.meta.env.MODE}`)
        if (this.isBlocked) {
          console.warn('Google Analytics: Requests may be blocked. Check ad blockers or privacy extensions.')
        }
      }
      
      this.isInitialized = true
    } catch (error) {
      console.error('Google Analytics initialization failed:', error)
      this.isBlocked = true
      // Don't throw - allow app to continue even if analytics fails
    }
  }

  private async loadGoogleAnalyticsScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      // Check if script is already loaded
      if (document.querySelector(`script[src*="googletagmanager.com/gtag/js"]`)) {
        resolve()
        return
      }

      const script = document.createElement('script')
      script.async = true
      script.src = `https://www.googletagmanager.com/gtag/js?id=${this.measurementId}`
      
      let resolved = false
      
      const timeout = setTimeout(() => {
        if (!resolved) {
          resolved = true
          this.isBlocked = true
          if (import.meta.env.DEV) {
            console.warn('Google Analytics: Script load timeout - may be blocked')
          }
          resolve() // Resolve instead of reject to allow app to continue
        }
      }, this.scriptLoadTimeout)

      script.onload = () => {
        if (!resolved) {
          resolved = true
          clearTimeout(timeout)
          resolve()
        }
      }
      
      script.onerror = () => {
        if (!resolved) {
          resolved = true
          clearTimeout(timeout)
          this.isBlocked = true
          if (import.meta.env.DEV) {
            console.warn('Google Analytics: Script failed to load - may be blocked by ad blocker')
          }
          resolve() // Resolve instead of reject to allow app to continue
        }
      }
      
      document.head.appendChild(script)
    })
  }

  private verifyInitialization(): void {
    // Check if dataLayer exists and is working
    if (!window.dataLayer || !Array.isArray(window.dataLayer)) {
      this.isBlocked = true
      if (import.meta.env.DEV) {
        console.warn('Google Analytics: dataLayer not properly initialized')
      }
    }
  }

  trackPageView(pagePath: string, pageTitle?: string): void {
    if (!this.isInitialized || !this.measurementId) return

    try {
      gtag('config', this.measurementId, {
        page_path: pagePath,
        page_title: pageTitle || document.title,
      })
    } catch (error) {
      // Silently handle errors (likely blocked by ad blocker)
      if (import.meta.env.DEV) {
        console.debug('Google Analytics: Page view tracking failed (may be blocked)', error)
      }
    }
  }

  trackEvent(eventName: string, parameters?: Record<string, any>): void {
    if (!this.isInitialized || !this.measurementId) return

    try {
      gtag('event', eventName, parameters)
    } catch (error) {
      // Silently handle errors (likely blocked by ad blocker)
      if (import.meta.env.DEV) {
        console.debug('Google Analytics: Event tracking failed (may be blocked)', error)
      }
    }
  }

  // Custom events for GRAP
  trackLogin(method: string = 'email'): void {
    this.trackEvent('login', {
      method: method
    })
  }

  trackSignup(method: string = 'email'): void {
    this.trackEvent('sign_up', {
      method: method
    })
  }

  trackSubstanceAdded(substanceName: string): void {
    this.trackEvent('substance_added', {
      substance_type: substanceName,
      event_category: 'substance_tracking'
    })
  }

  trackSubstanceUpdated(substanceName: string, changes: Record<string, any>): void {
    this.trackEvent('substance_updated', {
      substance_type: substanceName,
      event_category: 'substance_tracking',
      changes: changes
    })
  }

  trackSubstanceDeleted(substanceName: string): void {
    this.trackEvent('substance_deleted', {
      substance_type: substanceName,
      event_category: 'substance_tracking'
    })
  }

  trackReminderCreated(): void {
    this.trackEvent('reminder_created', {
      event_category: 'reminders'
    })
  }

  trackGoalCreated(goalType: string): void {
    this.trackEvent('goal_created', {
      goal_type: goalType,
      event_category: 'goals'
    })
  }

  trackInsightGenerated(): void {
    this.trackEvent('insight_generated', {
      event_category: 'ai_insights'
    })
  }

  trackChartViewed(chartType: string): void {
    this.trackEvent('chart_viewed', {
      chart_type: chartType,
      event_category: 'data_visualization'
    })
  }

  // Debug method to check current tracking configuration
  getTrackingInfo(): { trackingId: string; environment: string; isInitialized: boolean; isBlocked: boolean } {
    return {
      trackingId: this.measurementId || 'Not configured',
      environment: import.meta.env.MODE,
      isInitialized: this.isInitialized,
      isBlocked: this.isBlocked
    }
  }

  // Check if analytics is being blocked
  isAnalyticsBlocked(): boolean {
    return this.isBlocked
  }
}

export const analytics = new GoogleAnalytics()